import Credentials from '../model/Credentials';
import Token from '../model/Token';
import { makeAutoObservable } from 'mobx';
import SignIn from '../model/SignIn';
import AppService from '../services/AppService';
import axios from 'axios';
import { API_URL } from '../https';
import Constants from '../constants/constants';
import RefreshTokens from '../model/RefreshToken';
import { Counter } from '../model/Counter';
import { Attachment } from '../model/Attachment';
import { Category } from '../model/Category';
import { RejectionReason } from '../model/RejectionReason';
import { Municipality } from '../model/Municipality';
import { Appeal } from '../model/Appeal';
import { MunicipalityExecutors } from '../model/MunicipalityExecutors';
import { AppealAnswer } from '../model/AppealAnswer';
import { CitizenMessage } from '../model/CitizenMessage';
import { AppealPlacemark } from '../model/AppealPlacemark';
import { Page } from '../model/Page';
import { User } from '../model/User';
import { AppealForm } from '../model/form/AppealForm';
import { AppealStatus } from '../model/AppealStatus';
import { AppealCoordination } from '../model/AppealCoordination';
import { AppealApproval } from '../model/AppealApproval';
import { ExecutorAnswer } from '../model/ExecutorAnswer';
import { Register, RegisterError } from '../model/form/Register';
import { Status } from '../model/Status';
import { AppealAccept } from '../model/AppealAccept';
import {
  ChangePassword,
  Restore,
  RestoreError,
  RestoreSuccess,
} from '../model/form/Restore';

export default class Store {
  credentials = {} as Credentials;
  token = {} as Token;
  isAuth = false;
  currentTab = localStorage.getItem('activeTab') ?? '1';
  defaultTab = '1';
  isAdmin = false;

  constructor() {
    makeAutoObservable(this);
  }

  setAuth(auth: boolean): void {
    this.isAuth = auth;
  }

  setToken(token: Token): void {
    this.token = token;
  }

  async login(data: SignIn): Promise<void> {
    try {
      const response = await AppService.login(data);
      this.setToken(response.data);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      this.parseToken();
      this.setAuth(true);
    } catch (e) {
      throw e;
    }
  }

  async register(data: Register): Promise<Register | RegisterError> {
    try {
      const response = await AppService.register(data);
      return response.data;
    } catch (e) {
      const err: RegisterError = { error: e.response?.data?.message };
      return err;
    }
  }

  async restore(data: Restore): Promise<RestoreSuccess | RestoreError> {
    try {
      const response = await AppService.restorePassword(data);
      return response.data;
    } catch (e) {
      const err: RestoreError = { error: e.response?.data?.message };
      return err;
    }
  }

  async checkRestoreHash(hash: string): Promise<RestoreSuccess | RestoreError> {
    try {
      const response = await AppService.checkRestoreHash(hash);
      return response.data;
    } catch (e) {
      const err: RestoreError = { error: e.response?.data?.message };
      return err;
    }
  }

  async changePassword(
    changePassword: ChangePassword
  ): Promise<RestoreSuccess | RestoreError> {
    try {
      const response = await AppService.changePassword(changePassword);
      return response.data;
    } catch (e) {
      const err: RestoreError = { error: e.response?.data?.message };
      return err;
    }
  }

  logout(): void {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      this.resetStore();
    } catch (e) {}
  }

  async checkAuth(): Promise<void> {
    try {
      const token = localStorage.getItem('refresh_token');
      if (token !== null) {
        const data: RefreshTokens = {
          refresh_token: token as string,
        };

        const newData = Object.entries(data)
          .map(
            (x) =>
              `${encodeURIComponent(x[0])}=${encodeURIComponent(
                x[1] as string
              )}`
          )
          .join('&');
        const response = await axios.post<Token>(
          API_URL + 'token/refresh',
          newData,
          { withCredentials: true }
        );
        this.setToken(response.data);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        this.parseToken();
        this.setAuth(true);
      }
    } catch (e) {}
  }

  setCurrentTab(tab: string): void {
    localStorage.setItem('activeTab', tab);
    this.currentTab = tab;
  }

  private parseToken(): void {
    const credentials = JSON.parse(
      decodeURIComponent(
        Array.prototype.map
          .call(
            Store._urlB64ToUint8Array(this.token.token.split('.')[1]),
            (c) => {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }
          )
          .join('')
      )
    );
    credentials.token = this.token;
    this.credentials = credentials;
    this.credentials?.roles.map((userRole) => {
      const adminRoles = [
        Constants.moderatorRole,
        Constants.coordinatorRole,
        Constants.executorRole,
        Constants.directorRole,
        Constants.adminRole,
      ];
      if (adminRoles.includes(userRole)) {
        this.isAdmin = true;
        this.defaultTab = '3';
      }
    });
  }

  private static _urlB64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    return window.atob(base64);
  }

  public resetStore(): void {
    this.credentials = {} as Credentials;
    this.token = {} as Token;
    this.isAuth = false;
    this.currentTab = localStorage.getItem('activeTab') ?? '1';
    this.defaultTab = '1';
    this.isAdmin = false;
  }

  getUserId(): number {
    return this.credentials.id;
  }

  public hasRole(role: string): boolean {
    let hasRole = false;
    this.credentials?.roles.map((userRole) => {
      if (userRole === role) {
        hasRole = true;
      }
    });
    return hasRole;
  }

  async getCounters(): Promise<Counter | null> {
    try {
      const response = await AppService.getCounters();
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async upload(formData: FormData): Promise<Attachment | null> {
    try {
      const response = await AppService.upload(formData);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async deleteFile(id: number): Promise<Attachment | null> {
    try {
      const response = await AppService.deleteFile(id);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppealCategories(): Promise<Category[] | null> {
    try {
      const response = await AppService.getAppealCategories();
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getRejectionReasons(): Promise<RejectionReason[] | null> {
    try {
      const response = await AppService.getRejectionReasons();
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getMunicipalities(
    municipalityName?: string
  ): Promise<Municipality[] | null> {
    try {
      const response = await AppService.getMunicipalities(
        municipalityName ?? municipalityName
      );
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppealsList(author?: number): Promise<Appeal[] | null> {
    try {
      const response = await AppService.getAppealsList(author);
      return response.data;
    } catch (e) {
      return null;
    }
  }
  public async getAppealsListByRole(id?: number): Promise<Appeal[] | null> {
    try {
      const response = await AppService.getAppealsListByRole(id ?? undefined);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getCoordinators(): Promise<MunicipalityExecutors | null> {
    try {
      const response = await AppService.getCoordinators();
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppealAnswers(
    appealId?: number
  ): Promise<AppealAnswer[] | null> {
    try {
      const response = await AppService.getAppealAnswers(appealId);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppeal(appealId: number): Promise<CitizenMessage | null> {
    try {
      const response = await AppService.getAppeal(appealId);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppealAnswer(id: number): Promise<AppealAnswer | null> {
    try {
      const response = await AppService.getAppealAnswer(id);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppealSolvedAnswer(
    id: number
  ): Promise<AppealAnswer[] | null> {
    try {
      const response = await AppService.getAppealSolvedAnswer(id);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getAppealStatuses(isUser?: boolean): Promise<Status[] | null> {
    try {
      const response = await AppService.getAppealStatuses(isUser);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getPlacemarks(): Promise<AppealPlacemark[] | null> {
    try {
      const response = await AppService.getPlacemarks();
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getPage(id: number): Promise<Page | null> {
    try {
      const response = await AppService.getPage(id);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async getCurrentUserInfo(userId: number): Promise<User | null> {
    try {
      const response = await AppService.getCurrentUserInfo(userId);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async setAppeal(appeal: AppealForm): Promise<AppealForm | null> {
    try {
      const response = await AppService.setAppeal(appeal);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async setAppealModerationStatus(
    appealStatus: AppealStatus
  ): Promise<AppealStatus | null> {
    try {
      const response = await AppService.setAppealModerationStatus(appealStatus);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async setAppealCoordination(
    appealId: number,
    coordinationExecutors: AppealCoordination
  ): Promise<AppealStatus | null> {
    try {
      const response = await AppService.setAppealCoordination(
        appealId,
        coordinationExecutors
      );
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async setAppealApproval(
    appealAnswerId: number,
    appealApproval: AppealApproval
  ): Promise<AppealStatus | null> {
    try {
      const response = await AppService.setAppealApproval(
        appealAnswerId,
        appealApproval
      );
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async setAppealAnswer(
    executorAnswer: ExecutorAnswer
  ): Promise<ExecutorAnswer | null> {
    try {
      const response = await AppService.setAppealAnswer(executorAnswer);
      return response.data;
    } catch (e) {
      return null;
    }
  }

  public async setAppealAccept(appealId: number): Promise<AppealAccept | null> {
    try {
      const response = await AppService.setAppealAccept(appealId);
      return response.data;
    } catch (e) {
      return null;
    }
  }
}
